import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocRun,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  countWords,
  listOrPrompt,
  paragraphsOrPrompt,
  splitItems,
  splitLines,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A journal reviewer's report: a summary in the reviewer's own words, strengths, numbered
 * major comments (each with where it is, what is wrong, what would resolve it, and
 * whether the resolution is essential), minor comments, the scope of the review, a
 * recommendation with its reason, a confidential note to the editor, and the
 * declarations COPE's guidelines ask of a reviewer.
 *
 * The structure follows COPE's Ethical Guidelines for Peer Reviewers and the reviewer
 * guides of Elsevier and Springer Nature, which agree on the parts and differ on the
 * headings; a journal's own form always wins. The tool places the reviewer's sentences
 * as written and never judges the manuscript. Its checks look only at the report itself:
 * is it specific, does the recommendation square with the comments, does the wording
 * stay clear of personal remarks.
 */

export type Recommendation = "" | "accept" | "minor" | "major" | "reject";
export type Priority = "" | "essential" | "strengthen";

export interface MajorComment {
  id: string;
  /** Section, page or line: "Methods, p. 6, ll. 98–104". */
  location: string;
  concern: string;
  /** What would resolve it. */
  request: string;
  priority: Priority;
}

export interface PeerReviewReportInput {
  title: string;
  journal: string;
  manuscriptId: string;
  summary: string;
  strengths: string;
  major: MajorComment[];
  /** One minor comment per line. */
  minor: string;
  /** Parts of the manuscript the reviewer could not assess. */
  scope: string;
  recommendation: Recommendation;
  rationale: string;
  confidential: string;
  noConflict: boolean;
  keptConfidential: boolean;
  helpers: string;
}

export const MAX_MAJOR = 12;

export function emptyMajor(id: string): MajorComment {
  return { id, location: "", concern: "", request: "", priority: "" };
}

/** Words that read as personal rather than about the work. A prompt to look, not a verdict. */
const LOADED_WORDS: readonly string[] = [
  "stupid",
  "idiotic",
  "ridiculous",
  "pathetic",
  "incompetent",
  "sloppy",
  "lazy",
  "laughable",
  "nonsense",
  "worthless",
  "useless",
  "amateur",
  "clueless",
  "hopeless",
  "absurd",
  "garbage",
  "trash",
  "embarrassing",
  "awful",
  "terrible",
];
const LOADED_PATTERN = new RegExp(`\\b(?:${LOADED_WORDS.join("|")})\\b`, "gi");

function has(text: string): boolean {
  return text.trim() !== "";
}

function usedMajor(input: PeerReviewReportInput): [MajorComment, number][] {
  return input.major
    .map((comment, index): [MajorComment, number] => [comment, index + 1])
    .filter(([comment]) => has(comment.concern) || has(comment.request));
}

export function minorComments(input: PeerReviewReportInput): string[] {
  return splitItems(input.minor);
}

/** Words in everything the authors will read. */
export function authorWords(input: PeerReviewReportInput): number {
  const major = usedMajor(input).reduce(
    (sum, [c]) =>
      sum +
      countWords(c.location) +
      countWords(c.concern) +
      countWords(c.request),
    0
  );
  return (
    major +
    [
      input.summary,
      input.strengths,
      input.minor,
      input.scope,
      input.rationale,
    ].reduce((sum, text) => sum + countWords(text), 0)
  );
}

export interface ReportCounts {
  words: number;
  major: number;
  minor: number;
  essential: number;
}

export function reportCounts(input: PeerReviewReportInput): ReportCounts {
  const major = usedMajor(input);
  return {
    words: authorWords(input),
    major: major.length,
    minor: minorComments(input).length,
    essential: major.filter(([c]) => c.priority === "essential").length,
  };
}

/** Loaded words found in the author-facing text, lower-cased and de-duplicated. */
export function loadedWords(input: PeerReviewReportInput): string[] {
  const text = [
    input.summary,
    input.strengths,
    input.minor,
    input.scope,
    input.rationale,
    ...input.major.flatMap((c) => [c.concern, c.request]),
  ].join("\n");
  const found = (text.match(LOADED_PATTERN) ?? []).map((word) =>
    word.toLowerCase()
  );
  return [...new Set(found)];
}

export type ReportCheckId =
  | "summary"
  | "strengths"
  | "specific"
  | "priority"
  | "consistent"
  | "tone"
  | "declarations";

export interface ReportCheck {
  id: ReportCheckId;
  state: "done" | "todo" | "note";
  /** Which sentence describes the result (id + variant). */
  variant: string;
  /** Comment numbers, or the loaded words, the sentence refers to. */
  items?: string[];
}

function consistencyCheck(
  input: PeerReviewReportInput,
  counts: ReportCounts
): ReportCheck {
  const base = { id: "consistent" as const };
  switch (input.recommendation) {
    case "":
      return { ...base, state: "todo", variant: "none" };
    case "accept":
      return counts.major > 0
        ? { ...base, state: "note", variant: "acceptMajor" }
        : { ...base, state: "done", variant: "done" };
    case "minor":
      return counts.essential > 0
        ? { ...base, state: "note", variant: "minorEssential" }
        : { ...base, state: "done", variant: "done" };
    default:
      return counts.major === 0
        ? { ...base, state: "note", variant: "revisionNoMajor" }
        : { ...base, state: "done", variant: "done" };
  }
}

function listCheck(
  id: ReportCheckId,
  numbers: number[],
  okVariant = "done"
): ReportCheck {
  return numbers.length === 0
    ? { id, state: "done", variant: okVariant }
    : {
        id,
        state: "note",
        variant: "note",
        items: numbers.map(String),
      };
}

export function reportChecks(input: PeerReviewReportInput): ReportCheck[] {
  const counts = reportCounts(input);
  const major = usedMajor(input);
  const noWhere = major.filter(([c]) => !has(c.location)).map(([, n]) => n);
  const noPriority = major
    .filter(([c]) => has(c.request) && c.priority === "")
    .map(([, n]) => n);
  const loaded = loadedWords(input);
  const declared = input.noConflict && input.keptConfidential;
  return [
    {
      id: "summary",
      state: has(input.summary) ? "done" : "todo",
      variant: has(input.summary) ? "done" : "todo",
    },
    {
      id: "strengths",
      state: has(input.strengths) ? "done" : "note",
      variant: has(input.strengths) ? "done" : "note",
    },
    listCheck("specific", noWhere),
    listCheck("priority", noPriority),
    consistencyCheck(input, counts),
    loaded.length === 0
      ? { id: "tone", state: "done", variant: "done" }
      : { id: "tone", state: "note", variant: "note", items: loaded },
    {
      id: "declarations",
      state: declared ? "done" : "todo",
      variant: declared ? "done" : "todo",
    },
  ];
}

/* ------------------------------------------------------------------ document */

export interface PeerReviewReportLabels {
  defaultTitle: string;
  titleLead: string;
  subtitle: string;
  subtitleJournal: string;
  subtitleId: string;
  headings: {
    summary: string;
    strengths: string;
    major: string;
    minor: string;
    scope: string;
    recommendation: string;
    editor: string;
    declarations: string;
  };
  tips: {
    authors: string;
    summary: string;
    major: string;
    minor: string;
    recommendation: string;
    editor: string;
  };
  prompts: {
    summary: string;
    strengths: string;
    major: string;
    minor: string;
    scope: string;
    recommendation: string;
    rationale: string;
    editor: string;
    conflict: string;
    helpers: string;
  };
  majorLabel: string;
  majorPrompt: string;
  requestLead: string;
  priorities: { essential: string; strengthen: string };
  recommendationLine: string;
  recommendations: Record<Exclude<Recommendation, "">, string>;
  declarations: {
    noConflict: string;
    keptConfidential: string;
    helpers: string;
  };
}

function majorBlocks(
  comment: MajorComment,
  number: number,
  labels: PeerReviewReportLabels
): DocBlock[] {
  const where = comment.location.trim();
  const lead: DocRun = {
    text: t(labels.majorLabel, { n: number }),
    bold: true,
  };
  const heading: DocRun[] = where
    ? [lead, { text: ` (${where})`, bold: true }]
    : [lead];
  const concern = splitLines(comment.concern).join(" ");
  const request = splitLines(comment.request).join(" ");
  const blocks: DocBlock[] = [
    concern
      ? { kind: "paragraph", text: [...heading, { text: ` ${concern}` }] }
      : {
          kind: "prompt",
          text: [...heading, { text: ` ${labels.majorPrompt}` }],
        },
  ];
  if (request) {
    blocks.push({
      kind: "paragraph",
      text: [
        { text: `${labels.requestLead} `, italic: true },
        { text: request },
      ],
    });
  }
  if (comment.priority !== "") {
    blocks.push({
      kind: "paragraph",
      text: [{ text: labels.priorities[comment.priority], italic: true }],
    });
  }
  return blocks;
}

function authorSections(
  input: PeerReviewReportInput,
  labels: PeerReviewReportLabels
): DocSection[] {
  const major = usedMajor(input);
  const majorBody: DocBlock[] =
    major.length === 0
      ? [{ kind: "prompt", text: labels.prompts.major }]
      : major.flatMap(([comment, n]) => majorBlocks(comment, n, labels));
  const minor = minorComments(input);
  return [
    {
      blocks: [{ kind: "guidance", text: labels.tips.authors }],
    },
    {
      heading: labels.headings.summary,
      blocks: [
        { kind: "guidance", text: labels.tips.summary },
        ...paragraphsOrPrompt(input.summary, labels.prompts.summary),
      ],
    },
    {
      heading: labels.headings.strengths,
      blocks: paragraphsOrPrompt(input.strengths, labels.prompts.strengths),
    },
    {
      heading: labels.headings.major,
      blocks: [{ kind: "guidance", text: labels.tips.major }, ...majorBody],
    },
    {
      heading: labels.headings.minor,
      blocks: [
        { kind: "guidance", text: labels.tips.minor },
        ...(minor.length === 0
          ? [{ kind: "prompt", text: labels.prompts.minor } as DocBlock]
          : listOrPrompt(input.minor, "decimal", labels.prompts.minor)),
      ],
    },
    {
      heading: labels.headings.scope,
      blocks: paragraphsOrPrompt(input.scope, labels.prompts.scope),
    },
  ];
}

function recommendationSection(
  input: PeerReviewReportInput,
  labels: PeerReviewReportLabels
): DocSection {
  const blocks: DocBlock[] = [
    { kind: "guidance", text: labels.tips.recommendation },
  ];
  if (input.recommendation === "") {
    blocks.push({ kind: "prompt", text: labels.prompts.recommendation });
  } else {
    blocks.push({
      kind: "paragraph",
      text: [
        { text: `${labels.recommendationLine} `, bold: true },
        { text: labels.recommendations[input.recommendation] },
      ],
    });
  }
  blocks.push(...paragraphsOrPrompt(input.rationale, labels.prompts.rationale));
  return { heading: labels.headings.recommendation, blocks };
}

function declarationItems(
  input: PeerReviewReportInput,
  labels: PeerReviewReportLabels
): DocBlock[] {
  const helpers = splitLines(input.helpers).join("; ");
  return [
    {
      kind: "checklist",
      items: [
        { text: labels.declarations.noConflict, checked: input.noConflict },
        {
          text: labels.declarations.keptConfidential,
          checked: input.keptConfidential,
        },
      ],
    },
    ...(input.noConflict
      ? []
      : [{ kind: "prompt", text: labels.prompts.conflict } as DocBlock]),
    helpers
      ? {
          kind: "paragraph",
          text: [
            { text: `${labels.declarations.helpers} `, italic: true },
            { text: helpers },
          ],
        }
      : { kind: "prompt", text: labels.prompts.helpers },
  ];
}

export function buildPeerReviewReport(
  input: PeerReviewReportInput,
  labels: PeerReviewReportLabels
): DocumentModel {
  const title = input.title.trim();
  const journal = input.journal.trim();
  const id = input.manuscriptId.trim();
  const subtitleParts = [
    journal ? t(labels.subtitleJournal, { journal }) : "",
    id ? t(labels.subtitleId, { id }) : "",
  ].filter((part) => part !== "");
  return {
    title: title ? `${labels.titleLead} ${title}` : labels.defaultTitle,
    subtitle:
      subtitleParts.length > 0 ? subtitleParts.join(" · ") : labels.subtitle,
    sections: [
      ...authorSections(input, labels),
      recommendationSection(input, labels),
      {
        heading: labels.headings.editor,
        blocks: [
          { kind: "guidance", text: labels.tips.editor },
          ...paragraphsOrPrompt(input.confidential, labels.prompts.editor),
        ],
      },
      {
        heading: labels.headings.declarations,
        blocks: declarationItems(input, labels),
      },
    ],
  };
}
